import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, MODULE_GROUPS, APP_ROLES } from '../authConfig';
import { createGraphClient, checkMultipleGroupMemberships } from '../services/graphService';

export interface Permissions {
  admin: boolean;
  offboarding: boolean;
  eslSolum: boolean;
  odooImages: boolean;
  onboarding: boolean;
}

interface PermissionsContextValue {
  permissions: Permissions;
  isLoading: boolean;
  hasAnyAccess: boolean;
}

const defaultPermissions: Permissions = {
  admin: false,
  offboarding: false,
  eslSolum: false,
  odooImages: false,
  onboarding: false,
};

const PermissionsContext = createContext<PermissionsContextValue>({
  permissions: defaultPermissions,
  isLoading: true,
  hasAnyAccess: false,
});

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [permissions, setPermissions] = useState<Permissions>(defaultPermissions);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      if (inProgress !== InteractionStatus.None || accounts.length === 0) return;

      // Mode dev : aucun groupe configuré → accès total
      const allGroupIds = Object.values(MODULE_GROUPS).filter(Boolean);
      if (!allGroupIds.length) {
        setPermissions({ admin: true, offboarding: true, eslSolum: true, odooImages: true, onboarding: true });
        setIsLoading(false);
        return;
      }

      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });

        const client = createGraphClient(response.accessToken);
        const memberOf = await checkMultipleGroupMemberships(
          client,
          accounts[0].localAccountId,
          allGroupIds
        );

        const isAdmin = MODULE_GROUPS.admin ? (memberOf[MODULE_GROUPS.admin] ?? false) : false;
        const roles = (accounts[0].idTokenClaims?.roles as string[] | undefined) ?? [];

        setPermissions({
          admin: isAdmin,
          offboarding: isAdmin || (MODULE_GROUPS.offboarding ? (memberOf[MODULE_GROUPS.offboarding] ?? false) : false),
          eslSolum:    isAdmin || (MODULE_GROUPS.eslSolum    ? (memberOf[MODULE_GROUPS.eslSolum]    ?? false) : false),
          odooImages:  isAdmin || (MODULE_GROUPS.odooImages  ? (memberOf[MODULE_GROUPS.odooImages]  ?? false) : false),
          // Une seule personne RH : rôle d'application attribué directement, sans groupe
          onboarding:  isAdmin || roles.includes(APP_ROLES.rh),
        });
      } catch (error) {
        console.error('Erreur vérification permissions:', error);
        setPermissions(defaultPermissions);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermissions();
  }, [instance, accounts, inProgress]);

  const hasAnyAccess = Object.values(permissions).some(Boolean);

  return (
    <PermissionsContext.Provider value={{ permissions, isLoading, hasAnyAccess }}>
      {children}
    </PermissionsContext.Provider>
  );
};
